import { Download, HelpCircle, Plus, Trash2, Upload } from 'lucide-react'
import { useRef, useState } from 'react'
import { FaFacebookF } from 'react-icons/fa'

import { useLeadsStore as useAdminLeadsStore } from '@/modules/admin/stores/leads.store'
import { useSalesLeadsStore } from '@/modules/sales/stores/salesLeads.store'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { FieldError } from '@/shared/components/ui/field-error'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { getErrorMessage } from '@/shared/lib/errorMessage'
import {
  getBackendFieldErrors,
  parseZod,
  scrollToFirstError,
  toNumberOrUndefined,
} from '@/shared/lib/formValidation'
import { toast } from '@/shared/lib/toast'
import { cn } from '@/shared/lib/utils'

import { LEAD_SOURCES } from '../../constants/leads'
import { SOURCE_STYLES } from '../../constants/sourceStyles'
import { useLeads } from '../../hooks/useLeads'
import { leadFormSchema } from '../../schemas/lead.schema'
import { downloadLeadTemplate } from '../../utils/leadTemplate'

const SOURCE_CHOICES = {
  marketing: [
    LEAD_SOURCES.LINKEDIN,
    LEAD_SOURCES.FACEBOOK,
    LEAD_SOURCES.OFFLINE,
  ],
  admin: [LEAD_SOURCES.LINKEDIN, LEAD_SOURCES.FACEBOOK, LEAD_SOURCES.OFFLINE],
  sales: [LEAD_SOURCES.OFFLINE],
}

const INITIAL_FORM = {
  name: '',
  company: '',
  phone: '',
  email: '',
  source: LEAD_SOURCES.LINKEDIN,
  leadWarmth: 'cold',
  status: 'open',
  products: [
    {
      product: '',
      description: '',
      quantity: '',
      value: '',
    },
  ],
}

export function LeadForm({ lead, onCancel, onSuccess, module = 'marketing' }) {
  const fileInputRef = useRef(null)
  const submitLockRef = useRef(false)

  const [isInstructionsOpen, setIsInstructionsOpen] = useState(false)
  const [errors, setErrors] = useState({})

  const [formData, setFormData] = useState(() => ({
    name: lead?.name || '',
    company: lead?.company || '',
    phone: lead?.phone || '',
    email: lead?.email || '',
    status: lead?.status || 'open',
    products:
      lead?.products?.length > 0
        ? lead.products.map((p) => ({
            product: p.product || '',
            description: p.description || '',
            quantity: p.quantity ?? '',
            value: p.value ?? '',
          }))
        : [
            {
              product: '',
              description: '',
              quantity: '',
              value: '',
            },
          ],
    source:
      lead?.source ||
      (module === 'sales' ? LEAD_SOURCES.OFFLINE : LEAD_SOURCES.LINKEDIN),

    leadWarmth: lead?.leadWarmth || 'cold',
  }))

  const marketingStore = useLeads()
  const adminStore = useAdminLeadsStore()
  const salesStore = useSalesLeadsStore()

  const store =
    module === 'admin'
      ? adminStore
      : module === 'sales'
        ? salesStore
        : marketingStore

  const { createLead, updateLead, bulkUploadLeads, isSubmitting } = store

  const clearError = (key) => {
    setErrors((prev) => {
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }

  const handleChange = (event) => {
    const { name, value } = event.target
    clearError(name)

    let sanitized = value
    if (name === 'name') {
      sanitized = value.replace(/[^a-zA-Z0-9\s.'-]/g, '')
    } else if (name === 'company') {
      sanitized = value.replace(/[^a-zA-Z0-9\s.,&'()/-]/g, '')
    } else if (name === 'phone') {
      sanitized = value.replace(/\D/g, '').slice(0, 10)
    }

    setFormData((prev) => ({
      ...prev,
      [name]: sanitized,
    }))
  }

  const handleProductChange = (index, field, value) => {
    clearError(`products.${index}.${field}`)

    let sanitized = value
    if (field === 'product') {
      sanitized = value.replace(/[^a-zA-Z0-9\s.,'()-]/g, '')
    } else if (field === 'quantity') {
      sanitized = value.replace(/\D/g, '')
    } else if (field === 'value') {
      sanitized = value.replace(/[^0-9.]/g, '')
      // ensure only one decimal point
      const parts = sanitized.split('.')
      if (parts.length > 2) {
        sanitized = parts[0] + '.' + parts.slice(1).join('')
      }
    }

    setFormData((prev) => ({
      ...prev,
      products: prev.products.map((item, i) =>
        i === index
          ? {
              ...item,
              [field]: sanitized,
            }
          : item,
      ),
    }))
  }

  const handleAddProduct = () => {
    setFormData((prev) => ({
      ...prev,
      products: [
        ...prev.products,
        {
          product: '',
          description: '',
          quantity: '',
          value: '',
        },
      ],
    }))
  }

  const handleRemoveProduct = (index) => {
    setFormData((prev) => {
      if (prev.products.length <= 1) {
        return {
          ...prev,
          products: [{ product: '', description: '', quantity: '', value: '' }],
        }
      }
      return {
        ...prev,
        products: prev.products.filter((_, i) => i !== index),
      }
    })
  }

  const totalQuantity = formData.products.reduce(
    (sum, item) => sum + (Number(item.quantity) || 0),
    0,
  )

  const totalValue = formData.products.reduce(
    (sum, item) =>
      sum + (Number(item.quantity) || 0) * (Number(item.value) || 0),
    0,
  )

  const handleSubmit = async (event) => {
    event.preventDefault()

    // Sync lock — React isSubmitting alone is too late to stop double-clicks
    if (isSubmitting || submitLockRef.current) return
    submitLockRef.current = true

    // Filter out rows where product name is empty so leaving product info empty is allowed
    const filledProducts = formData.products
      .filter((item) => item.product && item.product.trim().length > 0)
      .map((item) => ({
        product: String(item.product || '').trim(),
        description: String(item.description || '').trim(),
        quantity: toNumberOrUndefined(item.quantity) ?? 1,
        value: toNumberOrUndefined(item.value) ?? 0,
      }))

    const parsed = parseZod(leadFormSchema, {
      ...formData,
      products: filledProducts,
    })

    if (!parsed.success) {
      submitLockRef.current = false
      setErrors(parsed.errors)
      toast.error(parsed.message)
      scrollToFirstError()
      return
    }

    try {
      setErrors({})

      const payload = {
        ...parsed.data,
        phone: parsed.data.phone?.trim() ? parsed.data.phone.trim() : null,
        email: parsed.data.email?.trim() ? parsed.data.email.trim() : null,
      }

      const leadId = lead?.id || lead?._id
      if (leadId) {
        await updateLead(leadId, payload)
      } else {
        const result = await createLead(payload)
        if (!result) return
      }

      onSuccess?.()

      if (!lead) {
        setFormData({
          ...INITIAL_FORM,
          source:
            module === 'sales' ? LEAD_SOURCES.OFFLINE : LEAD_SOURCES.LINKEDIN,
        })
      }
    } catch (error) {
      setErrors(getBackendFieldErrors(error))
    } finally {
      submitLockRef.current = false
    }
  }

  const handleBulkUpload = async (event) => {
    const file = event.target.files?.[0]

    if (!file) return

    try {
      if (!bulkUploadLeads) return

      await bulkUploadLeads(file)
      onSuccess?.()
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to upload leads.'))
    } finally {
      event.target.value = ''
    }
  }

  return (
    <form noValidate onSubmit={handleSubmit} className="mt-4 space-y-5">
      {/* Basic Lead Information */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="name">Full Name</Label>

          <Input
            id="name"
            name="name"
            placeholder="Jane Doe"
            value={formData.name}
            onChange={handleChange}
            aria-invalid={Boolean(errors.name)}
            data-invalid={errors.name ? 'true' : undefined}
            className={cn(
              'h-11 rounded-xl',
              errors.name && 'border-red-500 focus-visible:ring-red-500',
            )}
          />
          <FieldError message={errors.name} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="company">Company</Label>

          <Input
            id="company"
            name="company"
            placeholder="Acme Inc"
            value={formData.company}
            onChange={handleChange}
            aria-invalid={Boolean(errors.company)}
            data-invalid={errors.company ? 'true' : undefined}
            className={cn(
              'h-11 rounded-xl',
              errors.company && 'border-red-500 focus-visible:ring-red-500',
            )}
          />
          <FieldError message={errors.company} />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="phone">Phone</Label>
            <span className="text-[11px] text-muted-foreground">
              Optional if email provided
            </span>
          </div>

          <Input
            id="phone"
            name="phone"
            maxLength={10}
            placeholder="Enter 10-digit phone number"
            value={formData.phone}
            onChange={handleChange}
            aria-invalid={Boolean(errors.phone)}
            data-invalid={errors.phone ? 'true' : undefined}
            className={cn(
              'h-11 rounded-xl',
              errors.phone && 'border-red-500 focus-visible:ring-red-500',
            )}
          />
          <FieldError message={errors.phone} />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="email">Email</Label>
            <span className="text-[11px] text-muted-foreground">
              Optional if phone provided
            </span>
          </div>

          <Input
            id="email"
            name="email"
            type="email"
            placeholder="jane@acme.com"
            value={formData.email}
            onChange={handleChange}
            aria-invalid={Boolean(errors.email)}
            data-invalid={errors.email ? 'true' : undefined}
            className={cn(
              'h-11 rounded-xl',
              errors.email && 'border-red-500 focus-visible:ring-red-500',
            )}
          />
          <FieldError message={errors.email} />
        </div>

        {/* Products */}
        <div className="space-y-4 sm:col-span-2">
          <FieldError message={errors.products} />
          {formData.products.map((item, index) => {
            const itemQty = Number(item.quantity) || 0
            const itemVal = Number(item.value) || 0
            const itemTotal = itemQty * itemVal

            return (
              <div
                key={index}
                className="rounded-2xl border border-slate-200/80 bg-slate-50/50 p-3.5 space-y-2"
              >
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_85px_110px_115px_40px]">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                      Product
                    </Label>

                    <Input
                      name={`product-${index}`}
                      autoComplete="off"
                      placeholder="Macbook Pro"
                      value={item.product}
                      onChange={(event) =>
                        handleProductChange(
                          index,
                          'product',
                          event.target.value,
                        )
                      }
                      aria-invalid={Boolean(
                        errors[`products.${index}.product`],
                      )}
                      data-invalid={
                        errors[`products.${index}.product`] ? 'true' : undefined
                      }
                      className={cn(
                        'h-11 rounded-xl bg-white',
                        errors[`products.${index}.product`] &&
                          'border-red-500 focus-visible:ring-red-500',
                      )}
                    />
                    <FieldError message={errors[`products.${index}.product`]} />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                      Quantity
                    </Label>

                    <Input
                      type="number"
                      min={1}
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(event) =>
                        handleProductChange(
                          index,
                          'quantity',
                          event.target.value,
                        )
                      }
                      aria-invalid={Boolean(
                        errors[`products.${index}.quantity`],
                      )}
                      data-invalid={
                        errors[`products.${index}.quantity`]
                          ? 'true'
                          : undefined
                      }
                      className={cn(
                        'h-11 rounded-xl bg-white',
                        errors[`products.${index}.quantity`] &&
                          'border-red-500 focus-visible:ring-red-500',
                      )}
                    />
                    <FieldError
                      message={errors[`products.${index}.quantity`]}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                      Value
                    </Label>

                    <Input
                      type="text"
                      placeholder="Unit value"
                      value={item.value ?? ''}
                      onChange={(event) =>
                        handleProductChange(index, 'value', event.target.value)
                      }
                      aria-invalid={Boolean(errors[`products.${index}.value`])}
                      data-invalid={
                        errors[`products.${index}.value`] ? 'true' : undefined
                      }
                      className={cn(
                        'h-11 rounded-xl bg-white',
                        errors[`products.${index}.value`] &&
                          'border-red-500 focus-visible:ring-red-500',
                      )}
                    />
                    <FieldError message={errors[`products.${index}.value`]} />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                      Total Value
                    </Label>

                    <div className="flex h-11 items-center rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 shadow-xs">
                      ₹{itemTotal.toLocaleString('en-IN')}
                    </div>
                  </div>

                  <div className="flex items-end pb-0.5">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveProduct(index)}
                      className={cn(
                        'h-11 w-11 rounded-xl transition-colors',
                        formData.products.length > 1
                          ? 'text-red-500 hover:bg-red-50 hover:text-red-600'
                          : 'text-slate-400 hover:bg-slate-100 hover:text-red-500',
                      )}
                      title={
                        formData.products.length > 1
                          ? 'Remove product'
                          : 'Clear product'
                      }
                    >
                      <Trash2 className="h-5 w-5" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                    Product Description
                  </Label>
                  <Input
                    name={`description-${index}`}
                    autoComplete="off"
                    placeholder="Enter product description (optional)"
                    value={item.description}
                    onChange={(event) =>
                      handleProductChange(
                        index,
                        'description',
                        event.target.value,
                      )
                    }
                    className="h-11 rounded-xl bg-white"
                  />
                  <FieldError
                    message={errors[`products.${index}.description`]}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Source + Lead Classification + Total Summary / Add Product */}
      <div className="flex flex-col gap-4">
        {/* Total Summary Bar + Add Product */}
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200/80 bg-slate-50/80 p-3.5">
          <div className="flex flex-wrap items-center gap-4 text-xs">
            <div>
              <span className="font-medium text-slate-500">
                Total Quantity:{' '}
              </span>
              <span className="text-sm font-bold text-slate-900">
                {totalQuantity}
              </span>
            </div>
            <div className="hidden h-4 w-px bg-slate-300 sm:block" />
            <div>
              <span className="font-medium text-slate-500">Total Value: </span>
              <span className="text-sm font-bold text-indigo-600">
                ₹{totalValue.toLocaleString('en-IN')}
              </span>
            </div>
          </div>

          <Button
            type="button"
            variant="ghost"
            onClick={handleAddProduct}
            className="w-auto gap-2 bg-[#0F172A] px-5 text-white hover:bg-slate-800 hover:text-white"
          >
            <Plus className="h-4 w-4" />
            Add Product
          </Button>
        </div>

        {/* Source + Lead Classification */}
        <div className="space-y-5">
          {/* Source */}
          <div className="space-y-2">
            <Label>Source</Label>
            <FieldError message={errors.source} />

            <div className="flex flex-nowrap items-center gap-2">
              {(SOURCE_CHOICES[module] || SOURCE_CHOICES.marketing).map(
                (source) => {
                  const config = SOURCE_STYLES[source]
                  const selected = formData.source === source

                  return (
                    <button
                      key={source}
                      type="button"
                      onClick={() => {
                        clearError('source')
                        setFormData((prev) => ({
                          ...prev,
                          source,
                        }))
                      }}
                      className={cn(
                        'inline-flex h-10 items-center justify-center gap-2 rounded-full border px-4 text-sm font-medium transition-colors',
                        selected
                          ? 'bg-[#0F172A] text-white'
                          : 'border-slate-200 bg-white hover:bg-slate-50',
                      )}
                    >
                      {source === LEAD_SOURCES.LINKEDIN && (
                        <config.icon className="h-4 w-4" />
                      )}

                      {source === LEAD_SOURCES.FACEBOOK && (
                        <FaFacebookF className="h-4 w-4" />
                      )}

                      {source === LEAD_SOURCES.OFFLINE && (
                        <config.icon className="h-4 w-4" />
                      )}

                      {config.label}
                    </button>
                  )
                },
              )}
            </div>
          </div>

          {/* Lead Classification */}
          <div className="space-y-2">
            <Label>Lead Classification</Label>
            <FieldError message={errors.leadWarmth} />

            <div className="flex flex-nowrap items-center gap-2">
              {[
                {
                  value: 'hot',
                  label: 'Hot',
                  className:
                    'border-red-200 bg-red-50 text-red-700 hover:bg-red-100',
                },
                {
                  value: 'warm',
                  label: 'Warm',
                  className:
                    'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100',
                },
                {
                  value: 'cold',
                  label: 'Cold',
                  className:
                    'border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100',
                },
                {
                  value: 'unqualified',
                  label: 'Unqualified',
                  className:
                    'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100',
                },
              ].map((item) => {
                const selected = formData.leadWarmth === item.value

                return (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => {
                      clearError('leadWarmth')
                      setFormData((prev) => ({
                        ...prev,
                        leadWarmth: item.value,
                      }))
                    }}
                    className={cn(
                      'inline-flex h-10 items-center justify-center rounded-full border px-4 text-sm font-medium transition-colors',
                      selected ? 'bg-[#0F172A] text-white' : item.className,
                    )}
                  >
                    {item.label}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Status (shown when editing or in admin module) */}
          {(lead || module === 'admin') && (
            <div className="space-y-2">
              <Label>Status</Label>
              <FieldError message={errors.status} />

              <div className="flex flex-wrap items-center gap-2">
                {[
                  {
                    value: 'open',
                    label: 'Open',
                    className:
                      'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100',
                  },
                  {
                    value: 'in_progress',
                    label: 'In Progress',
                    className:
                      'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100',
                  },
                  {
                    value: 'won',
                    label: 'Won',
                    className:
                      'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
                  },
                  {
                    value: 'lost',
                    label: 'Lost',
                    className:
                      'border-red-200 bg-red-50 text-red-700 hover:bg-red-100',
                  },
                ].map((item) => {
                  const selected = formData.status === item.value

                  return (
                    <button
                      key={item.value}
                      type="button"
                      onClick={() => {
                        clearError('status')
                        setFormData((prev) => ({
                          ...prev,
                          status: item.value,
                        }))
                      }}
                      className={cn(
                        'inline-flex h-10 items-center justify-center rounded-full border px-4 text-sm font-medium transition-colors',
                        selected ? 'bg-[#0F172A] text-white' : item.className,
                      )}
                    >
                      {item.label}
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {!lead && <div className="my-6 border-t-2 border-black" />}

      {/* Download Template */}
      {!lead && (
        <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:px-5 sm:py-4">
          <p className="text-sm leading-5 text-slate-500 sm:text-base">
            Download the Excel template before uploading your leads.
          </p>

          <div className="mt-3 flex w-full flex-col gap-2 sm:flex-row">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsInstructionsOpen(true)}
              className="w-full rounded-full px-4 text-sm sm:w-auto"
            >
              <HelpCircle className="mr-2 size-4 shrink-0" />
              Instructions
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={downloadLeadTemplate}
              className="w-full rounded-full px-4 text-sm sm:w-auto"
            >
              <Download className="mr-2 size-4 shrink-0" />
              Download Template
            </Button>
          </div>
        </div>
      )}

      {/* Bulk Upload */}
      {!lead && (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-muted/30 px-5 py-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-700">Bulk Upload</p>

              <p className="text-sm text-muted-foreground">
                Import multiple leads from an Excel (.xlsx/.xls) or CSV file.
              </p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xls,.xlsx"
              className="hidden"
              onChange={handleBulkUpload}
            />

            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isSubmitting}
              onClick={() => fileInputRef.current?.click()}
              className="w-full rounded-full sm:w-auto"
            >
              <Upload className="mr-2 h-4 w-4" />
              Upload Excel
            </Button>
          </div>
        </div>
      )}

      {/* ============================= */}
      {/* BULK UPLOAD INSTRUCTIONS */}
      {/* ============================= */}
      <Dialog open={isInstructionsOpen} onOpenChange={setIsInstructionsOpen}>
        <DialogContent
          className="
            w-[calc(100%-40px)]
            max-w-[360px]
            h-[90vh]
            max-h-[90vh]
            overflow-hidden
            rounded-3xl
            px-5
            py-5
            sm:h-[85vh]
            sm:max-h-[85vh]
            sm:max-w-[450px]
            sm:px-6
            sm:py-6
          "
        >
          <DialogHeader className="shrink-0 space-y-1">
            <DialogTitle className="text-center text-lg font-semibold">
              Bulk upload instructions
            </DialogTitle>

            <DialogDescription className="text-center text-sm leading-5">
              How to fill the Excel template for multiple products.
            </DialogDescription>
          </DialogHeader>

          {/* Scroll ONLY the content */}
          <div className="min-h-0 flex-1 overflow-y-auto pr-1">
            <div className="space-y-4 pb-2 pt-2 text-sm text-slate-600">
              {/* Columns */}
              <div>
                <p className="font-semibold text-slate-900">Columns</p>

                <p className="mt-1 leading-5">
                  Full Name, Company, Phone, Email, Product, Quantity, Source,
                  Lead Warmth
                </p>
              </div>

              {/* Source */}
              <div>
                <p className="font-semibold text-slate-900">Source</p>

                <p className="mt-1 break-words leading-5">
                  {module === 'sales' ? (
                    <>
                      Must be: <code>offline</code>
                    </>
                  ) : (
                    <>
                      Must be one of: <code>linkedin</code>,{' '}
                      <code>facebook</code>, <code>offline</code>
                    </>
                  )}
                </p>
              </div>

              {/* Lead Warmth */}
              <div>
                <p className="font-semibold text-slate-900">Lead Warmth</p>

                <p className="mt-1 break-words leading-5">
                  Must be one of: <code>hot</code>, <code>warm</code>,{' '}
                  <code>cold</code>, <code>unqualified</code>
                </p>

                <div className="mt-3 rounded-2xl bg-slate-100 px-3 py-3 text-xs leading-5 text-slate-700">
                  <p>
                    <strong>Hot</strong> – Expected closure within{' '}
                    <strong>1 week</strong>
                  </p>

                  <p>
                    <strong>Warm</strong> – Expected closure within{' '}
                    <strong>1–2 months</strong>
                  </p>

                  <p>
                    <strong>Cold</strong> – Expected closure after{' '}
                    <strong>more than one quarter</strong>
                  </p>

                  <p>
                    <strong>Unqualified</strong> – Closure timeline unknown
                  </p>
                </div>
              </div>

              {/* Multiple Products */}
              <div>
                <p className="font-semibold text-slate-900">
                  Multiple products
                </p>

                <p className="mt-1 leading-5">
                  Use matching lists in Product and Quantity, separated by{' '}
                  <code>|</code>:
                </p>

                <div className="mt-2 overflow-x-auto rounded-2xl bg-slate-100 px-3 py-3">
                  <p className="whitespace-nowrap font-mono text-[11px] text-slate-800">
                    Product: MacBook Air | iPhone 17
                    <br />
                    Quantity: 2 | 5
                  </p>
                </div>
              </div>

              {/* Single Product */}
              <div>
                <p className="font-semibold text-slate-900">Single product</p>

                <p className="mt-1 break-words leading-5">
                  Product: <code>MacBook Air</code> · Quantity: <code>2</code>
                </p>
              </div>

              {/* Tip */}
              <p className="break-words pb-2 text-xs leading-5 text-slate-500">
                Tip: prefer <code>|</code> over commas so product names can
                include commas. Product and Quantity counts must match.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer Actions */}
      <div className="flex justify-stretch gap-3 border-t pt-5 sm:justify-end">
        <Button
          type="button"
          variant="outline"
          size="lg"
          className="flex-1 sm:flex-none"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>

        <Button
          type="submit"
          variant="ghost"
          disabled={isSubmitting}
          className="flex-1 gap-2 bg-[#0F172A] text-white hover:bg-slate-800 hover:text-white sm:flex-none"
        >
          {isSubmitting
            ? lead
              ? 'Updating...'
              : 'Creating...'
            : lead
              ? 'Update lead'
              : 'Create lead'}
        </Button>
      </div>
    </form>
  )
}
